// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { api, type VersionResponse } from '../api.js';

interface LayoutProps {
  children: ComponentChildren;
}

export function Layout({ children }: LayoutProps) {
  // Pull /version once for the footer's identity-pubkey fingerprint.
  // Fingerprint is one of the load-bearing trust signals: a user can
  // verify the identity pubkey shown here matches the one published
  // out-of-band (verifier operator's website, signed announcement,
  // etc.) before trusting any signed payload.
  const [version, setVersion] = useState<VersionResponse | null>(null);
  useEffect(() => {
    api.version().then(setVersion);
  }, []);

  return (
    <div class="layout">
      <div class="banner" role="status">
        Independent Verifier — operated by Cobault Independent Verifier
      </div>
      <header class="header">
        <a href="/" class="header-brand">
          <span class="header-brand-mark">●</span>Cobault Verifier
        </a>
        <nav class="header-nav">
          <a href="/">Lookup</a>
        </nav>
      </header>
      <main class="content">{children}</main>
      <footer class="footer">
        <div class="footer-line">
          Independent verifier surface. Holds one of three keys; signs
          withdrawals only when they match the contract stored at vault
          creation. Operator separate from Cobault user-app.
        </div>
        {version && (
          <>
            <div class="footer-line">
              Identity pubkey:{' '}
              <span class="footer-pubkey">{version.identity_pubkey_hex}</span>
            </div>
            {/* V13 / VO7 — commit hash as a phishing sanity-check. A
                copycat site can't fake a commit SHA that resolves on
                gitlab; vault owners about to enter a seed phrase
                should confirm this matches a known release. */}
            <div class="footer-line">
              Verifier build:{' '}
              <a
                class="footer-commit"
                href={`https://gitlab.com/chaincapital/cobault-verifier/-/commit/${version.commit}`}
                target="_blank"
                rel="noopener"
              >
                {version.commit}
              </a>
            </div>
          </>
        )}
      </footer>
    </div>
  );
}
